import { useCallback, useEffect, useMemo, useState } from 'react';
import socket from '../socket';
import { normalizeAttachmentKey } from '../utils/attachments';

export const defaultAttachmentState = {
    questionId: null,
    attachmentId: null,
    url: '',
    urlKey: '',
    visible: false,
    position: 'above',
    startAtSec: 0,
    currentTime: 0,
    duration: 0,
    state: 'stopped',
    updatedAt: 0,
};

const canonicalizeUrlKey = (value = '') => normalizeAttachmentKey(String(value || ''));

const buildAttachmentKey = (questionId, urlKey, attachmentId, url) => {
    const normalizedUrl = canonicalizeUrlKey(url || urlKey || attachmentId || '');
    return `${questionId ?? 'unknown'}::${normalizedUrl || attachmentId || 'unknown'}`;
};

const computeKeyParts = (questionId, payload = {}) => {
    const rawIdentifier = payload.urlKey ?? payload.url ?? payload.attachmentId ?? '';
    const urlKey = canonicalizeUrlKey(rawIdentifier);
    return {
        urlKey,
        key: buildAttachmentKey(questionId, urlKey, payload.attachmentId, payload.url),
    };
};

const normalizeStatePayload = (payload = {}) => {
    const flattened = payload.state && typeof payload.state === 'object' && !Array.isArray(payload.state)
        ? { ...payload, ...payload.state }
        : payload;
    const hasIdentifier = Object.prototype.hasOwnProperty.call(flattened, 'urlKey') ||
        Object.prototype.hasOwnProperty.call(flattened, 'url') ||
        Object.prototype.hasOwnProperty.call(flattened, 'attachmentId');
    const urlKey = hasIdentifier ? canonicalizeUrlKey(flattened.urlKey || flattened.url || flattened.attachmentId || '') : undefined;
    const hasStartAt =
        Object.prototype.hasOwnProperty.call(flattened, 'startAtSec') ||
        Object.prototype.hasOwnProperty.call(flattened, 'startAtSeconds') ||
        Object.prototype.hasOwnProperty.call(flattened, 'startAt');
    const startAtSec = flattened.startAtSec ?? flattened.startAtSeconds ?? flattened.startAt;
    const hasCurrentTime =
        Object.prototype.hasOwnProperty.call(flattened, 'currentTime') ||
        Object.prototype.hasOwnProperty.call(flattened, 'atSeconds');
    const rawCurrentTime = flattened.currentTime ?? flattened.atSeconds;
    const hasState = Object.prototype.hasOwnProperty.call(flattened, 'state') ||
        Object.prototype.hasOwnProperty.call(flattened, 'status');
    const mappedState = flattened.status
        ? (flattened.status === 'playing'
            ? 'playing'
            : flattened.status === 'paused'
                ? 'paused'
                : flattened.status === 'stopped'
                    ? 'stopped'
                    : flattened.state)
        : flattened.state;

    return {
        ...flattened,
        ...(urlKey ? { urlKey } : {}),
        ...(flattened.url ? { url: flattened.url } : {}),
        ...(hasStartAt ? { startAtSec } : {}),
        ...(hasCurrentTime ? { currentTime: rawCurrentTime } : {}),
        ...(hasState ? { state: mappedState ?? flattened.state } : {}),
        __hasCurrentTime: hasCurrentTime,
        __hasStartAt: hasStartAt,
    };
};

function useAttachmentControls() {
    const [controls, setControls] = useState({});

    useEffect(() => {
        const mergeCtrl = (prevCtrl, patch = {}) => {
            const defined = (value) => value !== undefined;
            const next = { ...prevCtrl };

            if (defined(patch.questionId)) next.questionId = patch.questionId;
            if (defined(patch.attachmentId)) next.attachmentId = patch.attachmentId;
            if (defined(patch.urlKey)) next.urlKey = patch.urlKey;
            if (defined(patch.url)) next.url = patch.url;
            if (defined(patch.visible)) next.visible = patch.visible;
            if (defined(patch.position)) next.position = patch.position;
            if (defined(patch.startAtSec)) next.startAtSec = patch.startAtSec;
            if (defined(patch.currentTime)) next.currentTime = patch.currentTime;
            if (defined(patch.state)) next.state = patch.state;
            if (defined(patch.duration)) next.duration = patch.duration;
            if (defined(patch.updatedAt)) next.updatedAt = patch.updatedAt;

            return next;
        };

        const mergeState = (payload = {}) => {
            const { key, urlKey } = computeKeyParts(payload.questionId, payload);
            setControls((prev) => {
                const existing = prev[key] || {
                    ...defaultAttachmentState,
                    questionId: payload.questionId,
                    attachmentId: payload.attachmentId,
                    url: payload.url || urlKey,
                    urlKey,
                };
                const { __hasCurrentTime, __hasStartAt, ...normalized } = normalizeStatePayload({ ...payload, urlKey });
                const incomingUpdatedAt = normalized.updatedAt;
                const shouldSkip = incomingUpdatedAt !== undefined && existing.updatedAt && incomingUpdatedAt <= existing.updatedAt;
                if (shouldSkip) return prev;

                const hasIncomingTime = __hasCurrentTime;
                const incomingState = normalized.state ?? existing.state;
                const incomingStartAt = __hasStartAt
                    ? normalized.startAtSec
                    : existing.startAtSec ?? defaultAttachmentState.startAtSec;
                const incomingCurrent = hasIncomingTime ? (normalized.currentTime ?? incomingStartAt) : undefined;
                const drift = hasIncomingTime && Number.isFinite(existing.currentTime)
                    ? Math.abs((existing.currentTime || 0) - (incomingCurrent || 0))
                    : null;

                let nextCurrentTime = existing.currentTime;
                if (hasIncomingTime) {
                    if (incomingState !== 'playing') {
                        nextCurrentTime = incomingCurrent ?? nextCurrentTime ?? incomingStartAt ?? 0;
                    } else if (drift === null || drift > 0.5 || !Number.isFinite(existing.currentTime)) {
                        nextCurrentTime = incomingCurrent ?? existing.currentTime ?? incomingStartAt ?? 0;
                    }
                }

                const basePatch = {
                    ...normalized,
                    ...(normalized.url ?? existing.url ? { url: normalized.url ?? existing.url } : {}),
                    urlKey: normalized.urlKey ?? existing.urlKey ?? urlKey,
                    ...(incomingUpdatedAt !== undefined ? { updatedAt: incomingUpdatedAt } : {}),
                    ...(__hasStartAt ? { startAtSec: incomingStartAt ?? 0 } : {}),
                };
                const merged = mergeCtrl(existing, basePatch);

                if (hasIncomingTime) {
                    merged.currentTime = nextCurrentTime ?? merged.currentTime ?? 0;
                }

                if (!hasIncomingTime && merged.currentTime == null) {
                    merged.currentTime = existing.currentTime ?? incomingStartAt ?? 0;
                }

                return { ...prev, [key]: merged };
            });
        };

        const mergeMany = (payload) => {
            if (!payload) return;
            if (Array.isArray(payload)) {
                payload.forEach(mergeState);
                return;
            }
            const statesField = payload?.states;
            if (statesField && Array.isArray(statesField)) {
                statesField.forEach((entry) => mergeState({ ...entry, questionId: payload.questionId ?? entry.questionId }));
                return;
            }
            if (payload?.statesByUrl && typeof payload.statesByUrl === 'object') {
                Object.entries(payload.statesByUrl).forEach(([stateUrlKey, state]) => {
                    mergeState({
                        ...state,
                        questionId: payload.questionId,
                        urlKey: canonicalizeUrlKey(stateUrlKey),
                        url: canonicalizeUrlKey(stateUrlKey),
                    });
                });
                return;
            }
            if (statesField && typeof statesField === 'object') {
                Object.entries(statesField).forEach(([stateUrlKey, state]) => {
                    mergeState({
                        ...state,
                        questionId: payload.questionId,
                        urlKey: canonicalizeUrlKey(stateUrlKey),
                        url: canonicalizeUrlKey(stateUrlKey),
                    });
                });
                return;
            }
            if (payload?.statesByUrl && typeof payload.statesByUrl === 'object') {
                Object.entries(payload.statesByUrl).forEach(([stateUrlKey, state]) => {
                    mergeState({
                        ...state,
                        questionId: payload.questionId,
                        urlKey: normalizeAttachmentKey(stateUrlKey),
                        url: normalizeAttachmentKey(stateUrlKey),
                    });
                });
                return;
            }
            mergeState(payload);
        };

        const handleCommand = (payload = {}) => {
            const { command } = payload;
            if (!command) {
                mergeState(normalizeStatePayload(payload));
                return;
            }
            const mappedState = command === 'play' ? 'playing' : command === 'pause' ? 'paused' : command === 'stop' ? 'stopped' : undefined;
            const normalized = normalizeStatePayload({
                ...payload,
                state: mappedState,
            });
            mergeState(normalized);
        };

        const handleTimeSync = (payload = {}) => {
            mergeState(normalizeStatePayload(payload));
        };

        const handleConnect = () => socket.emit('attachment:getState');

        const handlePlay = (payload) => handleCommand({ ...payload, command: 'play' });
        const handlePause = (payload) => handleCommand({ ...payload, command: 'pause' });
        const handleStop = (payload) => handleCommand({ ...payload, command: 'stop' });
        const handleSeek = (payload) => handleCommand({ ...payload, command: 'seek' });

        socket.on('attachment:state', mergeMany);
        socket.on('attachment:command', handleCommand);
        socket.on('attachment:play', handlePlay);
        socket.on('attachment:pause', handlePause);
        socket.on('attachment:stop', handleStop);
        socket.on('attachment:seek', handleSeek);
        socket.on('attachment:timeSync', handleTimeSync);
        socket.on('connect', handleConnect);
        socket.emit('attachment:getState');

        return () => {
            socket.off('attachment:state', mergeMany);
            socket.off('attachment:command', handleCommand);
            socket.off('attachment:play', handlePlay);
            socket.off('attachment:pause', handlePause);
            socket.off('attachment:stop', handleStop);
            socket.off('attachment:seek', handleSeek);
            socket.off('attachment:timeSync', handleTimeSync);
            socket.off('connect', handleConnect);
        };
    }, []);

    const getState = useMemo(
        () => (questionId, attachment = {}) => {
            const attachmentId = typeof attachment === 'string' ? attachment : attachment.id;
            const url = typeof attachment === 'string' ? null : attachment.url;
            const urlKey = normalizeAttachmentKey(
                typeof attachment === 'string' ? attachment : attachment.urlKey || attachment.url || attachment.id
            );
            const key = buildAttachmentKey(questionId, urlKey, attachmentId, url);
            return controls[key] || { ...defaultAttachmentState, questionId, attachmentId, url, urlKey };
        },
        [controls]
    );

    const optimisticMerge = useCallback((payload = {}) => {
        const { key, urlKey } = computeKeyParts(payload.questionId, payload);
        setControls((prev) => {
            const existing = prev[key] || {
                ...defaultAttachmentState,
                questionId: payload.questionId,
                attachmentId: payload.attachmentId,
                url: payload.url,
                urlKey,
            };
            const { __hasCurrentTime, ...normalized } = normalizeStatePayload({ ...payload, urlKey });
            const merged = {
                ...existing,
                ...defaultAttachmentState,
                ...normalized,
                updatedAt: Date.now(),
            };
            return { ...prev, [key]: merged };
        });
    }, []);

    const withUrlKey = useCallback(
        (payload = {}) => ({
            ...payload,
            urlKey: normalizeAttachmentKey(String(payload.urlKey || payload.url || payload.attachmentId || '')),
            url: normalizeAttachmentKey(String(payload.urlKey || payload.url || payload.attachmentId || '')),
        }),
        []
    );

    const buildPayload = useCallback((questionIdOrPayload, attachment, options = {}) => {
        if (
            attachment === undefined &&
            options === undefined &&
            questionIdOrPayload &&
            typeof questionIdOrPayload === 'object' &&
            !Array.isArray(questionIdOrPayload)
        ) {
            return questionIdOrPayload;
        }

        const questionId = questionIdOrPayload;
        const att = attachment || {};
        const attachmentId = typeof att === 'string' ? att : att.id;
        const url = typeof att === 'string' ? att : att.url;
        const urlKey = typeof att === 'string' ? att : att.urlKey || url || attachmentId;

        return {
            questionId,
            attachmentId,
            url,
            urlKey,
            ...options,
        };
    }, []);

    const actions = useMemo(
        () => ({
            setControl: (payload) => {
                const nextPayload = withUrlKey(payload);
                const startAtSeconds = payload.startAtSeconds ?? payload.startAtSec ?? payload.startAt ?? nextPayload.startAtSec;
                optimisticMerge({ ...nextPayload, startAtSec: startAtSeconds ?? 0 });
                socket.emit('attachment:setState', { ...nextPayload, startAtSeconds });
                socket.emit('attachment:set', { ...nextPayload, startAtSeconds });
            },
            play: (questionIdOrPayload, attachment, options) => {
                const payload = buildPayload(questionIdOrPayload, attachment, options);
                const nextPayload = withUrlKey(payload);
                const atSeconds = payload.atSeconds ?? payload.currentTime ?? payload.startAtSec ?? payload.startAtSeconds ?? nextPayload.startAtSec ?? 0;
                optimisticMerge({ ...nextPayload, state: 'playing', currentTime: atSeconds, startAtSec: nextPayload.startAtSec ?? payload.startAtSeconds ?? 0 });
                socket.emit('attachment:play', { ...nextPayload, atSeconds });
            },
            pause: (questionIdOrPayload, attachment, options) => {
                const payload = buildPayload(questionIdOrPayload, attachment, options);
                const nextPayload = withUrlKey(payload);
                const atSeconds = payload.atSeconds ?? payload.currentTime ?? payload.startAtSec ?? payload.startAtSeconds ?? nextPayload.startAtSec ?? 0;
                optimisticMerge({ ...nextPayload, state: 'paused', currentTime: atSeconds });
                socket.emit('attachment:pause', { ...nextPayload, atSeconds });
            },
            stop: (questionIdOrPayload, attachment, options) => {
                const payload = buildPayload(questionIdOrPayload, attachment, options);
                const nextPayload = withUrlKey(payload);
                const startAtSec = nextPayload.startAtSec ?? nextPayload.startAtSeconds ?? payload.startAtSeconds;
                optimisticMerge({
                    ...nextPayload,
                    state: 'stopped',
                    currentTime: startAtSec ?? 0,
                });
                socket.emit('attachment:stop', { ...nextPayload, startAtSeconds: startAtSec ?? 0 });
            },
            seek: (questionIdOrPayload, attachment, options) => {
                const payload = buildPayload(questionIdOrPayload, attachment, options);
                const nextPayload = withUrlKey(payload);
                const atSeconds = payload.atSeconds ?? payload.currentTime ?? payload.startAtSec ?? payload.startAtSeconds ?? nextPayload.startAtSec ?? 0;
                optimisticMerge({
                    ...nextPayload,
                    currentTime: atSeconds,
                });
                socket.emit('attachment:seek', { ...nextPayload, atSeconds });
            },
            timeSync: (questionIdOrPayload, attachment, options) => {
                const payload = buildPayload(questionIdOrPayload, attachment, options);
                const nextPayload = withUrlKey(payload);
                socket.emit('attachment:timeSync', {
                    ...nextPayload,
                    currentTime: payload.currentTime ?? payload.currentTimeSec,
                    duration: payload.duration ?? payload.durationSec,
                    updatedAt: payload.updatedAt,
                });
            },
        }),
        [buildPayload, optimisticMerge, withUrlKey]
    );

    return { controls, getState, actions };
}

export default useAttachmentControls;
